import { Injectable } from '@angular/core';

import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { Observable, map } from 'rxjs';

import { IBetslipRootState } from '../../../../base/store/state';
import { PickId } from '../../../../core/picks/pick-id';
import { BetslipGeneratorContextType, IBetslip } from '../../../bet-generation/models';
import { isStartingPriceSelected } from '../../../picks/services/betslip-pick-helpers';
import BetslipPickUtilsService from '../../../picks/services/betslip-pick-utils.service';
import { selectFreeBetTokenFactory } from '../../../reward-tokens/selectors';
import { ISlipSummary } from '../../../summary/models';
import { BetslipSummaryService } from '../../../summary/services/betslip-summary.service';
import { IContainerSummary, ILinearContainerSummaryService } from '../../../summary/services/linear/linear-container-summary.interface';
import { getBetslipPicks } from '../../../summary/utils/betslip-summary.utils';
import { selectSingleBetPickStakeFactory } from '../../selectors';

@Injectable()
export class SingleBetContainerSummaryService implements ILinearContainerSummaryService {
    private summary$: Observable<IContainerSummary>;

    constructor(
        private summaryService: BetslipSummaryService,
        private store: Store<IBetslipRootState>,
        private betslipPickUtils: BetslipPickUtilsService,
    ) {}

    init(pickId: PickId) {
        const stake$ = this.store.select(selectSingleBetPickStakeFactory(pickId));
        const freebet$ = this.store.select(selectFreeBetTokenFactory({ betslipType: BetslipType.Single, pickId }));

        const summary$: Observable<[ISlipSummary | null, IBetslip]> = this.summaryService
            .getSingleBetSummaryForPick$(BetslipGeneratorContextType.Betslip, pickId)
            .pipe(
                map(([betslip, betslipSummary]) => {
                    if (betslipSummary.slipSummaries.length !== 1) {
                        return [null, betslip];
                    }

                    return [betslipSummary.slipSummaries[0], betslip];
                }),
            );

        this.summary$ = summary$.pipe(
            concatLatestFrom(() => [stake$, freebet$]),
            map(([[summary, betslip], stake, freebet]) => {
                const hasStake = typeof stake?.stake === 'number' || !!freebet;
                const possibleWinnings = !!summary && hasStake ? summary.possibleWinnings : null;
                const boostedWinnings = summary?.rewardTokenModifications?.oddsBoost?.boostedWinnings ?? null;

                const picks = getBetslipPicks(betslip);

                const hasOddsUpdate = picks.some((pick) => this.betslipPickUtils.compareAgainstLastPrice(pick) !== 0);

                const isStartingPricePick = picks.some((pick) => isStartingPriceSelected(pick));

                return { possibleWinnings, boostedWinnings, hasOddsUpdate, showEmptyPossibleWinnings: isStartingPricePick && !possibleWinnings };
            }),
        );
    }

    getSummary$(): Observable<IContainerSummary> {
        return this.summary$;
    }
}
